import { useEffect, useRef, useState } from "react";
import { SectionHeading, SectionLabel } from "./Section";

export default function MoneyMeter() {
    const [progress, setProgress] = useState(0); // 0..1 across 72..81
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && !started.current) {
                        started.current = true;
                        const start = performance.now();
                        const dur = 1600;
                        const tick = (t) => {
                            const p = Math.min(1, (t - start) / dur);
                            const eased = 1 - Math.pow(1 - p, 3);
                            setProgress(eased);
                            if (p < 1) requestAnimationFrame(tick);
                        };
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.4 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const startScore = 72;
    const endScore = 81;
    const score = Math.round(startScore + (endScore - startScore) * progress);
    const r = 118;
    const c = 2 * Math.PI * r;
    const fillPct = 0.72 + 0.09 * progress; // 72% -> 81%

    return (
        <section
            id="money-meter"
            data-testid="money-meter-section"
            className="relative py-28 sm:py-36"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="reveal max-w-3xl">
                    <SectionLabel num="06">CEO Money Meter</SectionLabel>
                    <SectionHeading className="mt-6">
                        Know the money impact{" "}
                        <span className="italic font-normal">
                            before you approve
                        </span>
                        .
                    </SectionHeading>
                </div>

                <div ref={ref} className="reveal mt-16 grid lg:grid-cols-[1.1fr_1fr] gap-6 items-stretch">
                    {/* Gauge card */}
                    <div className="relative glass-strong rounded-3xl p-8 sm:p-12 flex flex-col items-center justify-center overflow-hidden">
                        <div className="pointer-events-none absolute inset-0"
                             style={{ background: "radial-gradient(60% 55% at 50% 60%, rgba(139,92,246,0.18), transparent 70%)" }}
                        />
                        <div className="relative">
                            <svg
                                width="300"
                                height="300"
                                viewBox="0 0 300 300"
                                data-testid="money-meter-gauge"
                                className="drop-shadow-[0_20px_40px_rgba(139,92,246,0.25)]"
                            >
                                <defs>
                                    <linearGradient id="meter-grad" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#A78BFA" />
                                        <stop offset="100%" stopColor="#7C3AED" />
                                    </linearGradient>
                                </defs>
                                <circle cx="150" cy="150" r={r} stroke="#F3F4F6" strokeWidth="14" fill="none" />
                                <circle
                                    cx="150"
                                    cy="150"
                                    r={r}
                                    stroke="url(#meter-grad)"
                                    strokeWidth="14"
                                    fill="none"
                                    strokeDasharray={c}
                                    strokeDashoffset={c * (1 - fillPct)}
                                    strokeLinecap="round"
                                    transform="rotate(-90 150 150)"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div
                                    data-testid="money-meter-score"
                                    className="font-display text-7xl sm:text-8xl font-medium text-black tracking-tight leading-none"
                                >
                                    {score}
                                </div>
                                <div className="mt-2 text-[11px] font-semibold tracking-[0.24em] uppercase text-neutral-500">
                                    Money Health
                                </div>
                                <div className="mt-1 inline-flex items-center gap-1 text-[12px] text-emerald-600 font-medium">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    +9 after decision
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats card */}
                    <div className="glass rounded-3xl p-8 flex flex-col">
                        <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-neutral-500">
                            Current Status
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-3">
                            {[
                                ["Cash", "₹18.4L"],
                                ["Revenue", "₹42L"],
                                ["Runway", "4.5 months"],
                                ["Money Health", "72 / 100"],
                            ].map(([k, v]) => (
                                <div key={k} className="rounded-2xl bg-white border border-black/5 p-4">
                                    <div className="text-[10px] font-medium tracking-[0.18em] uppercase text-neutral-500">
                                        {k}
                                    </div>
                                    <div className="mt-1.5 font-display text-xl font-medium text-black">
                                        {v}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 text-[11px] font-semibold tracking-[0.24em] uppercase text-violet-600">
                            Future Status
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3">
                            {[
                                ["After Decision", "81 / 100", "text-violet-600"],
                                ["Expected Recovery", "30 days", "text-black"],
                                ["Risk", "Low", "text-emerald-600"],
                            ].map(([k, v, t]) => (
                                <div key={k} className="rounded-2xl bg-white border border-black/5 p-4">
                                    <div className="text-[10px] font-medium tracking-[0.18em] uppercase text-neutral-500">
                                        {k}
                                    </div>
                                    <div className={`mt-1.5 font-display text-lg font-medium ${t}`}>
                                        {v}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-auto pt-8 flex items-center gap-3 text-[12px] text-neutral-500">
                            <span className="h-1.5 w-1.5 rounded-full bg-violet-500 anim-pulse-glow" />
                            Recalculated on every approve, simulate, or reject.
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
